import type { ReactNode } from "react";
import { fallbackCoverUrl } from "../lib/novel.js";
import { localizeGenre } from "../lib/storyOptions.js";
import { AuthorByline } from "./AuthorByline.js";
import type { NovelSummary } from "../apiClient.js";
import type { TranslateFn } from "../lib/i18n/store.js";
import { POSTER_ASPECT, POSTER_CSS_WIDTH, fallBackToSource, sizedImageUrl } from "../lib/imageUrl.js";
import { NOVEL_TRANSLATION_ENABLED } from "../lib/features.js";
import { languageChipCode, languageName } from "../lib/languageNames.js";
import { followInApp, novelHref } from "../lib/links.js";

/**
 * A single poster in a cinematic novel grid — cover-forward, with a hover
 * play affordance and a bottom credit block (title, genre tagline, reach).
 * Shared by the author filmography, the public catalog and the owner's "My
 * Novels". `onOpenAuthor` renders a clickable author byline in the bottom-right
 * corner (catalog only). `badge` renders a small top-left overlay (e.g. a draft
 * marker in My Novels).
 *
 * Deliberately no "AI" chip: listing surfaces are covered by the site-wide
 * notice (footer + catalog heading), and each novel carries its own "Created
 * with AI" label where it is read — the detail page and the player.
 *
 * Every target is a real link (lib/links.ts: in-app on a plain click, the
 * browser's own on a modified one), laid out as a stretched link: the credit
 * block is the whole-poster link to the detail page, and Play and the byline
 * are SIBLING links stacked above it — never one link inside another.
 */
export function FilmCard({
  novel,
  lang,
  t,
  onPlay,
  onOpenDetail,
  onOpenAuthor,
  badge,
  priority = false
}: {
  novel: NovelSummary;
  lang: string;
  t: TranslateFn;
  onPlay: (id: string) => void;
  onOpenDetail: (id: string) => void;
  onOpenAuthor?: (ownerId: string, username?: string) => void;
  badge?: ReactNode;
  /** The page's main image (the landing's featured card): load it at once and first, not lazily. */
  priority?: boolean;
}) {
  const extraLanguages = NOVEL_TRANSLATION_ENABLED ? (novel.available_languages ?? []) : [];
  // The novel's own language: a short code on the poster, its full name for
  // assistive tech and on hover.
  const languageCode = languageChipCode(novel.language);
  const languageLabel = languageCode ? languageName(languageCode.toLowerCase(), lang) : null;
  return (
    <article className="film-card">
      <img
        className="film-card-cover"
        src={sizedImageUrl(novel.cover_asset_url, POSTER_CSS_WIDTH, { aspect: POSTER_ASPECT }) ?? fallbackCoverUrl}
        onError={fallBackToSource(novel.cover_asset_url)}
        alt={novel.title}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : undefined}
        decoding="async"
      />
      {badge && <span className="film-badge">{badge}</span>}
      {languageCode && (
        <span className="film-lang" title={languageLabel ?? undefined}>
          <span aria-hidden="true">{languageCode}</span>
          <span className="sr-only">{t("catalog.cardLanguage", { language: languageLabel ?? languageCode })}</span>
        </span>
      )}
      {extraLanguages.length > 0 && (
        <span
          className="film-sub"
          title={t("common.localizedIn", { languages: extraLanguages.join(", ") })}
        >
          +{extraLanguages.length}
        </span>
      )}
      <a
        className="film-play"
        href={novelHref("player", novel)}
        onClick={(event) => followInApp(event, () => onPlay(novel.id))}
        aria-label={t("common.playTitle", { title: novel.title })}
        title={t("common.play")}
      >
        {t("common.play")}
      </a>
      <a
        className="film-body"
        href={novelHref("detail", novel)}
        onClick={(event) => followInApp(event, () => onOpenDetail(novel.id))}
        aria-label={t("catalog.viewDetails", { title: novel.title })}
      >
        <h2>{novel.title}</h2>
        {novel.genre.length > 0 && (
          <p className="film-tagline">{novel.genre.map((tag) => localizeGenre(tag, t)).join(" · ")}</p>
        )}
        <p className="film-reach">
          <span title={`${novel.views} views`}>◉ {novel.views.toLocaleString(lang)}</span>
          <span title={`${novel.likes} likes`}>♥ {novel.likes.toLocaleString(lang)}</span>
        </p>
      </a>
      {onOpenAuthor && (
        <AuthorByline
          ownerId={novel.owner_id}
          username={novel.owner_username}
          displayName={novel.owner_display_name}
          avatarUrl={novel.owner_avatar_url}
          onOpenAuthor={onOpenAuthor}
          className="film-byline"
        />
      )}
    </article>
  );
}
